import { useState, useEffect, useCallback } from "react";

const EYE_COMPONENTS_CHECK_STATUS = "eyeComponentsCheckStatus";

export interface EyeComponentCheckStatus {
  vision: {
    visualAcuity: boolean;
    autoRefraction: boolean;
    refraction: boolean;
    subjectiveRefraction: boolean;
    fieldOfVision: boolean;
    colorVision: boolean;
  };
  anteriorSegment: boolean;
  posteriorSegment: boolean;
  glassPrescription: boolean;
  lensPrescription: boolean;
}

export const defaultCheckStatus: EyeComponentCheckStatus = {
  vision: {
    visualAcuity: true,
    autoRefraction: true,
    refraction: true,
    subjectiveRefraction: true,
    fieldOfVision: true,
    colorVision: true
  },
  anteriorSegment: true,
  posteriorSegment: true,
  glassPrescription: true,
  lensPrescription: true
};

// Global state for sharing across hook instances
let globalCheckStatus: EyeComponentCheckStatus = defaultCheckStatus;
let listeners: Array<(s: EyeComponentCheckStatus) => void> = [];

// Initialize from localStorage once
if (typeof window !== "undefined") {
  try {
    const stored = localStorage.getItem(EYE_COMPONENTS_CHECK_STATUS);
    if (stored) {
      const parsed = JSON.parse(stored);
      globalCheckStatus = {
        ...defaultCheckStatus,
        ...parsed,
        vision: {
          ...defaultCheckStatus.vision,
          ...(parsed?.vision || {})
        }
      };
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("Failed to parse eye components check status", e);
  }
}

export function useEyeComponentCheckStatus(): {
  checkStatus: EyeComponentCheckStatus;
  setCheckStatus: (
    value: EyeComponentCheckStatus | ((prev: EyeComponentCheckStatus) => EyeComponentCheckStatus)
  ) => void;
} {
  const [checkStatus, setCheckStatusInner] = useState<EyeComponentCheckStatus>(globalCheckStatus);

  useEffect(() => {
    const listener = (s: EyeComponentCheckStatus) => {
      setCheckStatusInner(s);
    };
    listeners.push(listener);
    return () => {
      listeners = listeners.filter((l) => l !== listener);
    };
  }, []);

  const setCheckStatus = useCallback(
    (
      value: EyeComponentCheckStatus | ((prev: EyeComponentCheckStatus) => EyeComponentCheckStatus)
    ) => {
      const nextStatus = typeof value === "function" ? value(globalCheckStatus) : value;
      globalCheckStatus = nextStatus;

      if (typeof window !== "undefined") {
        localStorage.setItem(EYE_COMPONENTS_CHECK_STATUS, JSON.stringify(nextStatus));
      }

      // Notify all hook instances
      listeners.forEach((l) => l(nextStatus));
    },
    []
  );

  return { checkStatus, setCheckStatus };
}
